import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Award,
  ChevronLeft,
  ChevronRight,
  FileBadge,
  Globe,
  GraduationCap,
  Search,
  type LucideIcon,
} from 'lucide-react';
import ClassCard from '../components/ClassCard';
import MainTabsNav from '../components/MainTabsNav';

interface ClassItem {
  id: string;
  title: string;
  provider_id: string;
  provider_name: string;
  thumbnail_url: string | null;
  price: number;
  discount_price: number;
  start_date: string;
  end_date: string;
  location_type: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  location_address: string | null;
  class_type: 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER';
  status: 'SCHEDULED' | 'RECRUITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED';
  capacity: number;
  current_count: number;
  provider_business_category: string | null;
  created_at: string;
  bumped_at: string | null;
  closing_soon: boolean;
  is_premium?: boolean;
  category_path?: { id: string; name: string; level: number }[];
  instructors?: {
    id: string;
    name: string;
    description: string;
    image_url: string | null;
  }[];
}

type BusinessCategory = 'FITNESS' | 'PILATES';
type SortOption = 'latest' | 'oldest' | 'closing' | 'price_asc';

interface CategoryConfig {
  title: string;
  description: string;
  icon: LucideIcon;
  iconColorClass: string;
  filter: (cls: ClassItem) => boolean;
}

const categoryConfig: Record<string, CategoryConfig> = {
  online: {
    title: '온라인 교육 (100%)',
    description: '시간과 장소에 구애받지 않고 수강 가능한 온라인 수업을 모아 보여드립니다.',
    icon: Globe,
    iconColorClass: 'text-blue-500',
    filter: (c) => c.location_type === 'ONLINE',
  },
  certificate: {
    title: '자격 과정',
    description: '전문 자격증 취득을 목표로 하는 인증 과정을 모아 보여드립니다.',
    icon: Award,
    iconColorClass: 'text-accent',
    filter: (c) => c.class_type === 'CERTIFICATE',
  },
  completion: {
    title: '수료 과정',
    description: '커리큘럼 이수 시 수료증이 발급되는 교육 과정을 모아 보여드립니다.',
    icon: FileBadge,
    iconColorClass: 'text-blue-600',
    filter: (c) => c.class_type === 'COMPLETION',
  },
  workshop: {
    title: '워크샵 (세미나)',
    description: '단기간 집중적으로 진행되는 실전 워크샵을 모아 보여드립니다.',
    icon: GraduationCap,
    iconColorClass: 'text-purple-500',
    filter: (c) => c.class_type === 'WORKSHOP',
  },
};

const PAGE_SIZE = 12;

function getLocationText(cls: ClassItem): string {
  if (cls.location_type === 'ONLINE') return '온라인';
  if (cls.location_type === 'OFFLINE') return cls.location_address || '-';
  return '온/오프라인';
}

export default function ClassBoardPage() {
  const { category } = useParams<{ category: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const config = category ? categoryConfig[category] : undefined;

  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const businessTab = (searchParams.get('biz') as BusinessCategory) || 'FITNESS';
  const sort = (searchParams.get('sort') as SortOption) || 'latest';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  useEffect(() => {
    let cancelled = false;
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/v1/classes', { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch classes');
        const data = await response.json();
        if (!cancelled) setClasses(data.classes || []);
      } catch (error) {
        console.error('Failed to fetch classes:', error);
        if (!cancelled) setClasses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchClasses();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchClasses();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, []);

  const filteredAndSorted = useMemo(() => {
    if (!config) return [];
    const filtered = classes
      .filter(config.filter)
      .filter((c) => (c.provider_business_category || 'FITNESS') === businessTab)
      .filter((c) => {
        const q = searchQuery.toLowerCase();
        return c.title.toLowerCase().includes(q) || c.provider_name.toLowerCase().includes(q);
      });

    const sortKey = (cls: ClassItem) =>
      new Date(cls.bumped_at || cls.created_at).getTime();

    const sorted = [...filtered];
    switch (sort) {
      case 'oldest':
        sorted.sort((a, b) => sortKey(a) - sortKey(b));
        break;
      case 'closing':
        sorted.sort((a, b) => {
          if (a.closing_soon !== b.closing_soon) return a.closing_soon ? -1 : 1;
          return new Date(a.end_date).getTime() - new Date(b.end_date).getTime();
        });
        break;
      case 'price_asc':
        sorted.sort((a, b) => a.discount_price - b.discount_price);
        break;
      case 'latest':
      default:
        sorted.sort((a, b) => sortKey(b) - sortKey(a));
    }
    return sorted;
  }, [classes, config, businessTab, searchQuery, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filteredAndSorted.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: false });
  };

  if (!config) {
    return <Navigate to="/" replace />;
  }

  const Icon = config.icon;
  const pageTitle = `${config.title} 수업 모아보기 | StampFit`;

  return (
    <>
      <MainTabsNav />
      <div className="px-6 sm:px-10 lg:px-16 py-6">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={config.description} />
      </Helmet>

      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        전체 수업 목록으로
      </Link>

      <div className="bg-white rounded-2xl shadow-soft p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 rounded-lg bg-gray-50 ${config.iconColorClass}`}>
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{config.title}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{config.description}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => updateParam('biz', 'FITNESS')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            businessTab === 'FITNESS'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          피트니스
        </button>
        <button
          onClick={() => updateParam('biz', 'PILATES')}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            businessTab === 'PILATES'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          필라테스
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="수업명 검색, 교육기관 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => updateParam('sort', e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary focus:border-primary"
        >
          <option value="latest">최신순</option>
          <option value="oldest">오래된순</option>
          <option value="closing">마감임박순</option>
          <option value="price_asc">가격 낮은순</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : filteredAndSorted.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-soft">
          <p className="text-gray-500">해당 카테고리에 등록된 수업이 없습니다.</p>
          <Link
            to="/"
            className="inline-block mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            전체 수업 보러가기
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageItems.map((cls) => (
              <ClassCard
                key={cls.id}
                id={cls.id}
                title={cls.title}
                providerName={cls.provider_name}
                thumbnailUrl={
                  cls.thumbnail_url ||
                  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop'
                }
                price={cls.price}
                discountPrice={cls.discount_price}
                startDate={cls.start_date}
                endDate={cls.end_date}
                location={getLocationText(cls)}
                locationType={cls.location_type}
                classType={cls.class_type}
                status={cls.status === 'CLOSED' ? 'CLOSED' : cls.status}
                capacity={cls.capacity}
                currentCount={cls.current_count}
                closingSoon={cls.closing_soon}
                isPremium={cls.is_premium}
                categoryPath={cls.category_path}
                instructors={cls.instructors}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => updateParam('page', String(safePage - 1))}
                disabled={safePage <= 1}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="이전 페이지"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => updateParam('page', String(p))}
                  className={`min-w-[2rem] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                    p === safePage
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => updateParam('page', String(safePage + 1))}
                disabled={safePage >= totalPages}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="다음 페이지"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
      </div>
    </>
  );
}
